import { App } from 'antd';
import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '@/store/auth';

interface Props {
  /**
   * The authority codes the user needs to enter this route.
   * ANY match grants access. Use either a per-module code ('HR_MGMT:VIEW') or
   * a role authority ('ROLE_SUPER_ADMIN'). Omit to allow any authenticated user.
   */
  authority?: string | string[];
  /** Where to send the user when they don't have access. */
  redirectTo?: string;
  children: ReactNode;
}

export function RequireAuthority({ authority, redirectTo = '/dashboard', children }: Props) {
  const location = useLocation();
  const { message } = App.useApp();
  const user = useAuthStore((s) => s.user);
  const hasAuthority = useAuthStore((s) => s.hasAuthority);

  const codes = authority == null ? [] : Array.isArray(authority) ? authority : [authority];
  const allowed = codes.length === 0 ? user != null : codes.some((c) => hasAuthority(c));

  useEffect(() => {
    if (!allowed && user != null) {
      message.warning("You don't have access to that page");
    }
  }, [allowed, user, message]);

  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!allowed) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}
